import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { generateUUID } from 'src/lib/utils/helpers'
import { CustomerEligibilityOptions } from 'src/lib/types/common/customerConditions'
import {
  CreateMerchantSubscriptionRequest,
  GetSubscriptionResponse,
  MerchantSubscriptionPlan,
  MerchantSubscriptionPlanRequest,
  PlanIntervalUnit,
  SubscriptionFormState,
  SubscriptionPlanDraft,
  UpdateMerchantSubscriptionRequest
} from 'src/lib/types/subscriptions'
import {
  DELTA_DEFAULT,
  DURATION_DEFAULT,
  INTERVAL_UNIT_LABELS,
  MIN_CYCLES,
  PLAN_UNIT_MAX
} from './subscriptionConstants'

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/**
 * UUID, not a counter: Fast Refresh resets a module-level counter while React
 * keeps state, so two rows end up sharing a localId.
 */
export const createLocalId = () => `plan-draft-${generateUUID()}`

/** Starts with no discount: PERCENTAGE + null would be invalid on arrival. */
export const createEmptyPlanDraft = (rank: number): SubscriptionPlanDraft => ({
  localId: createLocalId(),
  intervalCount: 1,
  intervalUnit: 'MONTH',
  // A real default, not a placeholder. Clearing the field means "until cancelled".
  durationValue: DURATION_DEFAULT,
  discountType: 'NONE',
  discountValue: null,
  isRecommended: false,
  status: 'ENABLED',
  rank,
  isCommitted: false
})

export const getInitialSubscriptionFormState = (): SubscriptionFormState => ({
  name: '',
  description: '',
  products: [],
  mandateDeltaEnabled: true,
  mandateDelta: DELTA_DEFAULT,
  status: 'ENABLED',
  plans: [],
  editingPlanLocalId: null,
  customerEligibility: { type: CustomerEligibilityOptions.ALL_CUSTOMERS },
  enabledChannelIds: [],
  errors: {}
})

export const durationToMaxCycles = (durationValue: number | null, intervalCount: number | null) => {
  if (!durationValue || !intervalCount) return null
  return durationValue
}

export const maxCyclesToDuration = (maxCycles: number | null, _intervalCount: number) => {
  if (!maxCycles) return null
  return maxCycles
}

export const validDurations = (intervalCount: number | null, unit: PlanIntervalUnit): number[] => {
  if (!intervalCount || intervalCount < 1) return []

  const max = PLAN_UNIT_MAX[unit]
  const durations: number[] = []

  for (let cycles = MIN_CYCLES; intervalCount * cycles <= max; cycles += 1) {
    durations.push(intervalCount * cycles)
  }

  return durations
}

/** Closest legal duration to what was typed, for the "did you mean" in the error. */
export const nearestValidDuration = (durationValue: number, intervalCount: number | null, unit: PlanIntervalUnit) => {
  const options = validDurations(intervalCount, unit)
  if (!options.length) return null

  return options.reduce((closest, option) =>
    Math.abs(option - durationValue) < Math.abs(closest - durationValue) ? option : closest
  )
}

export const isDurationDivisible = (durationValue: number | null, intervalCount: number | null) => {
  if (!durationValue || !intervalCount) return true
  return durationValue % intervalCount === 0
}

export const formatIntervalUnit = (unit: PlanIntervalUnit, count: number | null, t: TranslateFn) =>
  t(`subscriptions.interval_${unit.toLowerCase()}`, { count: count ?? 0 })

/** "Deliver every 2 months" */
export const formatPlanFrequency = (plan: SubscriptionPlanDraft, t: TranslateFn) => {
  if (!plan.intervalCount) return ''
  const unit = formatIntervalUnit(plan.intervalUnit, plan.intervalCount, t)
  return plan.intervalCount === 1 ? unit : t('subscriptions.count_with_unit', { count: plan.intervalCount, unit })
}

export const formatPlanDuration = (plan: SubscriptionPlanDraft, t: TranslateFn) => {
  if (!plan.durationValue) return null
  return t('subscriptions.count_with_unit', {
    count: plan.durationValue,
    unit: formatIntervalUnit(plan.intervalUnit, plan.durationValue, t)
  })
}

/** "20% off" / "₹200 off" / null when there is no discount */
export const formatPlanDiscount = (plan: SubscriptionPlanDraft, t: TranslateFn) => {
  if (plan.discountType === 'NONE' || !plan.discountValue) return null
  return plan.discountType === 'PERCENTAGE'
    ? t('subscriptions.discount_percentage', { value: plan.discountValue })
    : t('subscriptions.discount_flat', { value: plan.discountValue })
}

/** Preview line: "Every month for 12 months" */
export const formatPlanSummary = (plan: SubscriptionPlanDraft, t: TranslateFn) => {
  const frequency = formatPlanFrequency(plan, t)
  if (!frequency) return ''
  const duration = formatPlanDuration(plan, t)
  return duration
    ? t('subscriptions.plan_summary', { frequency, duration })
    : t('subscriptions.plan_summary_unlimited', { frequency })
}

export const isPlanDraftComplete = (plan: SubscriptionPlanDraft) => {
  if (!plan.intervalCount || plan.intervalCount <= 0) return false
  if (plan.discountType !== 'NONE' && (!plan.discountValue || plan.discountValue <= 0)) return false
  if (!isDurationDivisible(plan.durationValue, plan.intervalCount)) return false
  return true
}

/** Discount excluded: the API's mutual-exclusivity rule ignores it. */
const planFrequencyKey = (plan: SubscriptionPlanDraft) =>
  `${plan.intervalCount}-${plan.intervalUnit}-${plan.durationValue ?? 'unlimited'}`

export const findConflictingPlanLocalIds = (plans: SubscriptionPlanDraft[]) => {
  const seen = new Map<string, string>()
  const conflicting: string[] = []

  plans
    .filter(plan => plan.status === 'ENABLED')
    .forEach(plan => {
      const key = planFrequencyKey(plan)
      const existing = seen.get(key)
      if (existing) {
        conflicting.push(plan.localId)
        if (!conflicting.includes(existing)) conflicting.push(existing)
        return
      }
      seen.set(key, plan.localId)
    })

  return conflicting
}

/** Checked on commit: per-row rules cannot see siblings. */
export const conflictsWithAnotherPlan = (plans: SubscriptionPlanDraft[], localId: string) => {
  const target = plans.find(plan => plan.localId === localId)
  if (!target || target.status !== 'ENABLED') return false

  const key = planFrequencyKey(target)

  return plans.some(plan => plan.localId !== localId && plan.status === 'ENABLED' && planFrequencyKey(plan) === key)
}

/** The API takes a flat variant list, so per-product selection is flattened. */
export const catalogueToVariantIds = (products: CatalogueDataType[]): string[] =>
  products
    .filter(product => !!product.id)
    .flatMap(product => (product.variants ?? []).map(variant => variant.id as string))
    .filter(Boolean)

/** Products carrying at least one selected variant. Used for validation. */
export const productsWithVariants = (products: CatalogueDataType[]) =>
  products.filter(product => !!product.id && (product.variants ?? []).some(variant => !!variant.id))

export const getClaimedVariantIds = (claimed: string[] | undefined, ownVariantIds: string[] = []) => {
  const own = new Set(ownVariantIds)

  return new Set((Array.isArray(claimed) ? claimed : []).filter(variantId => Boolean(variantId) && !own.has(variantId)))
}

const toPlanRequest = (plan: SubscriptionPlanDraft): MerchantSubscriptionPlanRequest => {
  const maxCycles = durationToMaxCycles(plan.durationValue, plan.intervalCount)

  return {
    interval_count: plan.intervalCount as number,
    interval_unit: plan.intervalUnit,
    ...(maxCycles ? { plan_duration: maxCycles } : {}),
    discount_type: plan.discountType,
    discount_value: plan.discountType === 'NONE' ? 0 : (plan.discountValue as number),
    recommended: plan.isRecommended,
    status: plan.status
  }
}

/**
 * `mandate_delta` is absent: not a create-request field. customerEligibility and
 * enabledChannelIds are not in the contract yet.
 */
export const createSubscriptionPayload = (state: SubscriptionFormState): CreateMerchantSubscriptionRequest => ({
  name: state.name.trim(),
  ...(state.description.trim() ? { description: state.description.trim() } : {}),
  variant_ids: catalogueToVariantIds(state.products),
  plans: state.plans.map(toPlanRequest)
})

/**
 * Every plan in form state, carrying its server id where it has one. That is what
 * makes full-replace work: a removed plan is absent, so the service disables it.
 */
export const updateSubscriptionPayload = (state: SubscriptionFormState): UpdateMerchantSubscriptionRequest => ({
  name: state.name.trim(),
  ...(state.description.trim() ? { description: state.description.trim() } : {}),
  variant_ids: catalogueToVariantIds(state.products),
  status: state.status,
  ...(state.mandateDeltaEnabled && state.mandateDelta ? { mandate_delta: state.mandateDelta } : {}),
  plans: state.plans.map(plan => ({
    ...(plan.id ? { id: plan.id } : {}),
    ...toPlanRequest(plan)
  }))
})

export const planToRequest = toPlanRequest

const planToDraft = (plan: MerchantSubscriptionPlan): SubscriptionPlanDraft => ({
  localId: createLocalId(),
  id: plan.id,
  intervalCount: plan.interval_count,
  intervalUnit: plan.interval_unit,
  durationValue: maxCyclesToDuration(plan.plan_duration, plan.interval_count),
  discountType: plan.discount_type,
  discountValue: plan.discount_type === 'NONE' ? null : plan.discount_value,
  isRecommended: plan.recommended,
  status: plan.status,
  rank: plan.rank,
  isCommitted: true
})

/** Variants are parked under a placeholder row until the catalogue lookup resolves them. */
export const PENDING_VARIANT_HYDRATION = 'pending-variant-hydration'

export const parseSubscriptionResponse = (response: GetSubscriptionResponse): Partial<SubscriptionFormState> => {
  const { subscription, plans } = response

  return {
    id: subscription.id,
    name: subscription.name,
    description: subscription.description ?? '',
    products: (subscription.variant_ids ?? []).length
      ? [
          {
            id: PENDING_VARIANT_HYDRATION,
            variants: (subscription.variant_ids ?? []).map(variantId => ({ id: variantId, isVariant: true }))
          }
        ]
      : [],
    mandateDelta: subscription.mandate_delta ?? null,
    mandateDeltaEnabled: subscription.mandate_delta != null,
    status: subscription.status,
    plans: plans
      .filter(plan => plan.status !== 'DISABLED')
      .sort((a, b) => a.rank - b.rank)
      .map(planToDraft)
  }
}

/**
 * Save is gated on a comparison against what the server returned on load, so a
 * rename does not also fire a reorder. Keys rather than objects, so optional
 * shapes (absent vs null, unsorted ids) cannot read as a change.
 */
export interface SubscriptionSaveBaseline {
  subscription: string
  /** planId -> { request key, rank } */
  plans: Record<string, { request: string; rank: number }>
}

export const subscriptionDiffKey = (payload: UpdateMerchantSubscriptionRequest) =>
  JSON.stringify({
    name: payload.name,
    description: payload.description ?? '',
    status: payload.status,
    mandate_delta: payload.mandate_delta ?? null,
    // Order is not meaningful in a variant list, and hydration does not preserve it.
    variant_ids: [...payload.variant_ids].sort(),
    // Plans ride along in the same call, so they belong in the same key. Keyed by
    // id so reordering alone does not read as an edit — that is the reorder call.
    plans: payload.plans
      .map(plan => ({ id: plan.id ?? null, key: planDiffKey(plan) }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
  })

export const planDiffKey = (request: MerchantSubscriptionPlanRequest) =>
  JSON.stringify({
    interval_count: request.interval_count,
    interval_unit: request.interval_unit,
    plan_duration: request.plan_duration ?? null,
    discount_type: request.discount_type,
    discount_value: request.discount_value,
    recommended: request.recommended,
    status: request.status
  })

const serverPlanToRequest = (plan: MerchantSubscriptionPlan): MerchantSubscriptionPlanRequest => ({
  interval_count: plan.interval_count,
  interval_unit: plan.interval_unit,
  ...(plan.plan_duration ? { plan_duration: plan.plan_duration } : {}),
  discount_type: plan.discount_type,
  discount_value: plan.discount_value,
  recommended: plan.recommended,
  status: plan.status
})

export const createSaveBaseline = (response: GetSubscriptionResponse): SubscriptionSaveBaseline => {
  const { subscription, plans } = response

  return {
    subscription: subscriptionDiffKey({
      name: subscription.name,
      description: subscription.description ?? '',
      variant_ids: subscription.variant_ids ?? [],
      status: subscription.status,
      ...(subscription.mandate_delta != null ? { mandate_delta: subscription.mandate_delta } : {}),
      plans: (plans ?? []).map(plan => ({ id: plan.id, ...serverPlanToRequest(plan) }))
    }),
    plans: Object.fromEntries(
      (plans ?? []).map(plan => [plan.id, { request: planDiffKey(serverPlanToRequest(plan)), rank: plan.rank }])
    )
  }
}

/** API errors carry a server message, or nothing when the caller should show its own copy. */
export const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback
